import { Providers } from "@microsoft/mgt-react";
import axios from "axios";
import CryptoJS from "crypto-js";
import { apiCallRequest } from "../features/Auth/authConfig";
import Helper from "../core/libraries/Helper";
import { IDivision } from "../features/Admin/Managements/Division/types";
import { IDropdownOption } from "@fluentui/react";
import Constants from "../core/libraries/Constants";
import { ICoordinator, IFeberAPIResponse } from "./type";


export const getDivisionsList = async () => {
    try {
        const divisions = await feberApiGet<IDivision[]>("Division");
        let rs: IDropdownOption[] = [];
        divisions.forEach((division) => {
            rs.push({
                key: division.title || "",
                text: division.longTitle || "",
            });
        });
        rs.unshift({ key: "All", text: "--- All ---" });
        Helper.setSessionStorageWithExpiry(Constants.SESSION_STORAGE_KEYS.divisionDropdownItems, rs, 10);
        return { success: true, data: divisions };
    } catch (error: any) {
        console.error("Failed to get divisions list:", error);
        throw error;
    }
}

export const FeberApiClient = axios.create({
    baseURL: process.env.REACT_APP_FEBER_API_BASE_URL,
    headers: {
        "Access-Control-Allow-Origin": "*",
    }
})

FeberApiClient.interceptors.request.use(async (config) => {
    const accessToken = await Providers.globalProvider.getAccessToken(
        {
            scopes: apiCallRequest.scopes
        });
    if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
},
    (error) => {
        return Promise.reject(error);
    })

FeberApiClient.interceptors.response.use(
    response => {
        // Return the response as-is for successful requests
        return response;
    }, 
    error => {
        console.error("API Error:", error);
        // Reject the error so it can be caught by calling code
        return Promise.reject(error);
    }
);

/**
 * Helper function to convert parameters object to URLSearchParams
 * Handles arrays by appending multiple values with the same key
 * @param params Object containing query parameters
 * @returns URLSearchParams object
 */
export const buildQueryParams = (params: Record<string, any>): URLSearchParams => {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
        if (value === null || value === undefined) {
            // Skip null/undefined values
            return;
        }
        
        if (Array.isArray(value)) {
            // For arrays, append each item with the same key
            value.forEach(item => {
                if (item !== null && item !== undefined) {
                    searchParams.append(key, item.toString());
                }
            });
        } else {
            // For non-array values, append as string
            searchParams.append(key, value.toString());
        }
    });
    
    return searchParams;
};

/**
 * Helper function to handle IFeberAPIResponse format
 * @param response Axios response containing IFeberAPIResponse
 * @returns The data if success is true
 * @throws Error with message if success is false
 */
export const handleFeberApiResponse = <T = any>(response: { data: IFeberAPIResponse }): T => {
    const apiResponse = response.data;
    
    if (apiResponse.success) {
        return apiResponse.data as T;
    } else {
        throw new Error(apiResponse.message || "API request failed");
    }
};

/**
 * Generic GET request with IFeberAPIResponse handling
 * Automatically handles array parameters for proper query string formatting
 * @param endpoint API endpoint
 * @param params Query parameters (arrays will be properly formatted)
 * @returns Unwrapped data
 */
export const feberApiGet = async <T = any>(endpoint: string, params?: Record<string, any>): Promise<T> => {
    try {
        // Check if params contain arrays and convert them properly
        const hasArrayParams = params && Object.values(params).some(value => Array.isArray(value));
        const queryParams = hasArrayParams && params ? buildQueryParams(params) : params;
        
        const response = await FeberApiClient.get(endpoint, { 
            params: queryParams,
            paramsSerializer: hasArrayParams ? undefined : undefined
        });
        return handleFeberApiResponse<T>(response);
    } catch (error: any) {
        if (error.response?.data?.message) {
            throw new Error(error.response.data.message);
        }
        throw error;
    }
};

/**
 * Generic POST request with IFeberAPIResponse handling
 * @param endpoint API endpoint
 * @param data Request body
 * @returns Unwrapped data
 */
export const feberApiPost = async <T = any>(endpoint: string, data?: any): Promise<T> => {
    try {
        const response = await FeberApiClient.post(endpoint, data);
        return handleFeberApiResponse<T>(response);
    } catch (error: any) {
        if (error.response?.data?.message) {
            throw new Error(error.response.data.message);
        }
        throw error;
    }
};

/**
 * Generic PUT request with IFeberAPIResponse handling
 * @param endpoint API endpoint
 * @param data Request body
 * @returns Unwrapped data
 */
export const feberApiPut = async <T = any>(endpoint: string, data?: any): Promise<T> => {
    try {
        const response = await FeberApiClient.put(endpoint, data);
        return handleFeberApiResponse<T>(response);
    } catch (error: any) {
        if (error.response?.data?.message) {
            throw new Error(error.response.data.message);
        }
        throw error;
    }
};

/**
 * Generic PATCH request with IFeberAPIResponse handling
 * @param endpoint API endpoint
 * @param data Request body
 * @returns Unwrapped data
 */
export const feberApiPatch = async <T = any>(endpoint: string, data?: any): Promise<T> => {
    try {
        const response = await FeberApiClient.patch(endpoint, data);
        return handleFeberApiResponse<T>(response);
    } catch (error: any) {
        if (error.response?.data?.message) {
            throw new Error(error.response.data.message);
        }
        throw error;
    }
};

/**
 * Generic DELETE request with IFeberAPIResponse handling
 * Automatically handles array parameters for proper query string formatting
 * @param endpoint API endpoint
 * @param params Query parameters (arrays will be properly formatted)
 * @returns Unwrapped data
 */
export const feberApiDelete = async <T = any>(endpoint: string, params?: Record<string, any>): Promise<T> => {
    try {
        // Check if params contain arrays and convert them properly
        const hasArrayParams = params && Object.values(params).some(value => Array.isArray(value));
        const queryParams = hasArrayParams && params ? buildQueryParams(params) : params;
        
        const response = await FeberApiClient.delete(endpoint, { 
            params: queryParams,
            paramsSerializer: hasArrayParams ? undefined : undefined
        });
        return handleFeberApiResponse<T>(response);
    } catch (error: any) {
        if (error.response?.data?.message) {
            throw new Error(error.response.data.message);
        }
        throw error;
    }
};

export const downloadReport = async (reportId: string, reportTitle: string, generatedName: string) => {
    try {
        let response = await FeberApiClient.get("Report/DownloadAttachment", {
            params: {
                filename: reportTitle,
                reportId: reportId
            },
            responseType: 'blob',
        })

        // Check if the response is a JSON (error message)
        const contentType = response.headers['content-type'];
        console.log(contentType);
        if (contentType && contentType.includes('application/json')) {
            // Read the blob as text
            const text = await response.data.message;
            throw new Error("Failed to download file: " + text);
        }

        const url = window.URL.createObjectURL(response.data); // Directly use response.data as it's already a Blob
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', generatedName);
        // Append link to body, simulate a click, then remove it
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url); // Clean up the object URL
        return true; // Indicate success
    } catch (error) {
        console.error("Error downloading file:", error);
        return false; // Indicate failure
    }
}

export const getCoordinator = async (uploadType: string, division: string): Promise<ICoordinator> => {
    try {
        return await feberApiGet<ICoordinator>("Division/GetCoordinator", {
            uploadType,
            division
        });
    } catch (error: any) {
        console.error("Failed to get coordinator:", error);
        throw error;
    }
}

export const exportLLDocument = async (reportId: string) => {
    try {
        return await feberApiGet("Report/ExportLL", { reportId });
    } catch (error: any) {
        console.error("Failed to export LL document:", error);
        throw error;
    }
}

export const encryptString = (text: string, key: string) => {
    return CryptoJS.AES.encrypt(text, key).toString();
}

export const decryptString = (encryptedText: string, key: string) => {
    const bytes = CryptoJS.AES.decrypt(encryptedText, key);
    return bytes.toString(CryptoJS.enc.Utf8);
}

export const checkFeatureAccess = async (featureName: string): Promise<boolean> => {
    try {
        return await feberApiGet<boolean>("Configuration/HasFeatureAccess", { featureName });
    } catch (error: any) {
        console.error(`Failed to check feature access for '${featureName}':`, error);
        throw error;
    }
}